import despia from 'despia-native';

import { isDespiaIOS } from '@/hooks/use-despia';

const DEFAULT_SETTLE_DELAY_MS = 300;
const LEGACY_IOS_SETTLE_DELAY_MS = 650;
const LAUNCH_LOCK_RELEASE_MS = 8000;

type RevenueCatLaunchState = {
  inFlight: boolean;
  lastUrl: string | null;
  releaseTimer: number | null;
  prewarmed: boolean;
};

declare global {
  interface Window {
    __revenueCatLaunchState?: RevenueCatLaunchState;
  }
}

interface LaunchNativeRevenueCatPaywallOptions {
  externalId?: string;
  offering?: string;
  settleDelayMs?: number;
  logPrefix?: string;
  onBeforeLaunch?: () => void | Promise<void>;
  onLaunchError?: (error: unknown) => void;
}

interface LaunchNativeRevenueCatPaywallResult {
  duplicate: boolean;
  iosVersion: number | null;
  launched: boolean;
  paywallUrl: string;
}

function getRevenueCatLaunchState(): RevenueCatLaunchState {
  if (typeof window === 'undefined') {
    return {
      inFlight: false,
      lastUrl: null,
      releaseTimer: null,
      prewarmed: false,
    };
  }

  if (!window.__revenueCatLaunchState) {
    window.__revenueCatLaunchState = {
      inFlight: false,
      lastUrl: null,
      releaseTimer: null,
      prewarmed: false,
    };
  }

  return window.__revenueCatLaunchState;
}

function scheduleRevenueCatLaunchRelease() {
  if (typeof window === 'undefined') return;

  const state = getRevenueCatLaunchState();
  if (state.releaseTimer !== null) {
    window.clearTimeout(state.releaseTimer);
  }

  state.releaseTimer = window.setTimeout(() => {
    state.inFlight = false;
    state.lastUrl = null;
    state.releaseTimer = null;
  }, LAUNCH_LOCK_RELEASE_MS);
}

export function parseDespiaIOSVersion(): number | null {
  if (typeof navigator === 'undefined') return null;

  const userAgent = navigator.userAgent || '';
  console.log('[parseDespiaIOSVersion] UA:', userAgent);
  const match = userAgent.match(/OS (\d+)[._]/i) ?? userAgent.match(/Version\/(\d+)/i);
  if (!match) {
    console.log('[parseDespiaIOSVersion] No iOS version match found in UA');
    return null;
  }

  const version = Number(match[1]);
  console.log('[parseDespiaIOSVersion] Parsed version:', version);
  return Number.isFinite(version) ? version : null;
}

function getSettleDelay(settleDelayMs?: number) {
  if (typeof settleDelayMs === 'number') return settleDelayMs;

  const iosVersion = parseDespiaIOSVersion();
  if (isDespiaIOS() && iosVersion !== null && iosVersion < 26) {
    return LEGACY_IOS_SETTLE_DELAY_MS;
  }

  return DEFAULT_SETTLE_DELAY_MS;
}

function buildRevenueCatPaywallUrl(externalId?: string, offering = 'default') {
  if (externalId) {
    return `revenuecat://launchPaywall?external_id=${externalId}&offering=${offering}`;
  }

  return `revenuecat://launchPaywall?offering=${offering}`;
}

function buildRevenueCatDirectPurchaseUrl(externalId: string, product: string) {
  return `revenuecat://purchase?external_id=${externalId}&product=${product}`;
}

export function resetRevenueCatLaunchLock() {
  if (typeof window === 'undefined') return;

  const state = getRevenueCatLaunchState();
  if (state.releaseTimer !== null) {
    window.clearTimeout(state.releaseTimer);
  }

  state.inFlight = false;
  state.lastUrl = null;
  state.releaseTimer = null;
}

export function cleanupPageMediaForNativePaywall(logPrefix = '[RevenueCat]') {
  if (typeof document === 'undefined') return;

  try {
    document.querySelectorAll('video, audio').forEach((element) => {
      const media = element as HTMLMediaElement;
      if (!media.paused) {
        media.pause();
      }

      media.removeAttribute('src');
      media.removeAttribute('poster');
      media.load();
    });

    document
      .querySelectorAll('img[src*="supabase"], img[src*="storage"]')
      .forEach((element) => {
        const image = element as HTMLImageElement;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = '';
      });

    console.log(`${logPrefix} Media cleanup complete before native paywall launch`);
  } catch (error) {
    console.warn(`${logPrefix} Media cleanup failed before native paywall launch`, error);
  }
}

export async function launchNativeRevenueCatPaywall({
  externalId,
  offering = 'default',
  settleDelayMs,
  logPrefix = '[RevenueCat]',
  onBeforeLaunch,
  onLaunchError,
}: LaunchNativeRevenueCatPaywallOptions): Promise<LaunchNativeRevenueCatPaywallResult> {
  // If the caller did not pass an externalId (anonymous / pre-signup flow),
  // fall back to the deterministic anonymous id bound at app launch. That id
  // already has `$appsflyerId` attached, so the purchase event RC fires to
  // AppsFlyer will carry the UID and forward correctly to Meta.
  let effectiveExternalId = externalId;
  if (!effectiveExternalId && typeof window !== 'undefined') {
    effectiveExternalId =
      (window as unknown as Record<string, string | undefined>).__rcAnonExternalId;
    if (effectiveExternalId) {
      console.log(`${logPrefix} Using anon RC external_id for paywall launch`, {
        externalId: effectiveExternalId,
      });
    }
  }

  const paywallUrl = buildRevenueCatPaywallUrl(effectiveExternalId, offering);
  const iosVersion = parseDespiaIOSVersion();
  const delayMs = getSettleDelay(settleDelayMs);
  const state = getRevenueCatLaunchState();

  if (state.inFlight) {
    console.warn(`${logPrefix} Native paywall launch skipped because another launch is already in flight`, {
      iosVersion,
      lastUrl: state.lastUrl,
      paywallUrl,
    });

    return {
      duplicate: true,
      iosVersion,
      launched: false,
      paywallUrl,
    };
  }

  state.inFlight = true;
  state.lastUrl = paywallUrl;

  console.log(`${logPrefix} Native paywall launch requested`, {
    iosVersion,
    paywallUrl,
    settleDelayMs: delayMs,
  });

  try {
    await onBeforeLaunch?.();
  } catch (error) {
    console.warn(`${logPrefix} Native paywall pre-launch step failed`, error);
  }

  if (delayMs > 0) {
    await new Promise((resolve) => window.setTimeout(resolve, delayMs));
  }

  try {
    const result = despia(paywallUrl);
    scheduleRevenueCatLaunchRelease();
    console.log(`${logPrefix} Native paywall command sent`, { iosVersion, paywallUrl });

    if (result && typeof result.catch === 'function') {
      result.catch((error: unknown) => {
        console.error(`${logPrefix} Native paywall command rejected`, error);
        resetRevenueCatLaunchLock();
        onLaunchError?.(error);
      });
    }

    return {
      duplicate: false,
      iosVersion,
      launched: true,
      paywallUrl,
    };
  } catch (error) {
    console.error(`${logPrefix} Native paywall command threw`, error);
    resetRevenueCatLaunchLock();
    onLaunchError?.(error);

    return {
      duplicate: false,
      iosVersion,
      launched: false,
      paywallUrl,
    };
  }
}

// ─── Direct Purchase (bypasses native paywall renderer) ───

interface LaunchNativeRevenueCatDirectPurchaseOptions {
  externalId: string;
  product: string;
  settleDelayMs?: number;
  logPrefix?: string;
  onBeforeLaunch?: () => void | Promise<void>;
  onLaunchError?: (error: unknown) => void;
}

export async function launchNativeRevenueCatDirectPurchase({
  externalId,
  product,
  settleDelayMs,
  logPrefix = '[RevenueCat]',
  onBeforeLaunch,
  onLaunchError,
}: LaunchNativeRevenueCatDirectPurchaseOptions): Promise<{ launched: boolean; purchaseUrl: string }> {
  const purchaseUrl = buildRevenueCatDirectPurchaseUrl(externalId, product);
  const delayMs = getSettleDelay(settleDelayMs);
  const state = getRevenueCatLaunchState();

  if (state.inFlight) {
    console.warn(`${logPrefix} Direct purchase skipped — already in flight`);
    return { launched: false, purchaseUrl };
  }

  state.inFlight = true;
  state.lastUrl = purchaseUrl;

  console.log(`${logPrefix} Direct purchase requested`, { product, purchaseUrl, settleDelayMs: delayMs });

  try {
    await onBeforeLaunch?.();
  } catch (error) {
    console.warn(`${logPrefix} Direct purchase pre-launch step failed`, error);
  }

  if (delayMs > 0) {
    await new Promise((resolve) => window.setTimeout(resolve, delayMs));
  }

  try {
    const result = despia(purchaseUrl);
    scheduleRevenueCatLaunchRelease();
    console.log(`${logPrefix} Direct purchase command sent`, { product, purchaseUrl });

    if (result && typeof result.catch === 'function') {
      result.catch((error: unknown) => {
        console.error(`${logPrefix} Direct purchase command rejected`, error);
        resetRevenueCatLaunchLock();
        onLaunchError?.(error);
      });
    }

    return { launched: true, purchaseUrl };
  } catch (error) {
    console.error(`${logPrefix} Direct purchase command threw`, error);
    resetRevenueCatLaunchLock();
    onLaunchError?.(error);
    return { launched: false, purchaseUrl };
  }
}

// ─── Prewarm ───
//
// Fire-and-forget hint to the RC native bridge that a paywall is coming.
// Sends a `login` (to bind the external_id) and a `preloadOfferings`
// command. Both are bridge no-ops if the SDK isn't ready, so this is
// safe to call early in the funnel. Guarded so we only do it once per
// session — subsequent calls are no-ops.

interface PrewarmOptions {
  externalId?: string;
  offering?: string;
  logPrefix?: string;
}

export function prewarmNativeRevenueCatPaywall({
  externalId,
  offering = 'default',
  logPrefix = '[RevenueCat]',
}: PrewarmOptions): void {
  if (!isDespiaIOS()) return;

  const state = getRevenueCatLaunchState();
  if (state.prewarmed) return;
  state.prewarmed = true;

  console.log(`${logPrefix} Prewarming native paywall`, { externalId, offering });

  try {
    if (externalId) {
      const loginUrl = `revenuecat://login?external_id=${encodeURIComponent(externalId)}`;
      const loginResult = despia(loginUrl);
      if (loginResult && typeof loginResult.catch === 'function') {
        loginResult.catch((err: unknown) => {
          console.warn(`${logPrefix} Prewarm login rejected`, err);
        });
      }
    }

    const preloadUrl = `revenuecat://preloadOfferings?offering=${encodeURIComponent(offering)}`;
    const preloadResult = despia(preloadUrl);
    if (preloadResult && typeof preloadResult.catch === 'function') {
      preloadResult.catch((err: unknown) => {
        // preloadOfferings may not be implemented by the despia bridge —
        // that's fine, the launch will still work, just without a warm cache.
        console.warn(`${logPrefix} Prewarm preloadOfferings rejected (non-fatal)`, err);
      });
    }
  } catch (err) {
    console.warn(`${logPrefix} Prewarm threw (non-fatal)`, err);
    // Don't keep prewarmed=true if we threw before issuing the command —
    // allow a retry next time.
    state.prewarmed = false;
  }
}